import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Renders the "Frequently bought together" block: keeps the running total in
 * sync with the checked items, and adds every checked item to the cart in a
 * single request when the button is clicked.
 *
 * @typedef {object} FrequentlyBoughtTogetherRefs
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} [totalDisplay]
 * @property {HTMLTemplateElement} [moneyFormat]
 * @property {HTMLElement} [liveRegion]
 * @property {HTMLInputElement[]} [checkboxes]
 * @extends Component<FrequentlyBoughtTogetherRefs>
 */
export class FrequentlyBoughtTogetherComponent extends Component {
  requiredRefs = ['addButton'];

  connectedCallback() {
    super.connectedCallback();
    this.#updateTotal();
  }

  /**
   * Called on checkbox change (declarative `on:change`).
   */
  handleItemToggle() {
    this.#updateTotal();
  }

  /**
   * Called on the "Add selected to cart" button click (declarative `on:click`).
   */
  async handleAddToCart() {
    const { addButton, liveRegion } = this.refs;
    const checked = this.#getCheckedCheckboxes();

    if (checked.length === 0) return;

    const items = checked
      .map((checkbox) => ({ id: Number(checkbox.dataset.variantId), quantity: 1 }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0);

    if (items.length === 0) return;

    addButton.disabled = true;
    addButton.setAttribute('data-loading', 'true');

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'frequently-bought-together',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (data.status) {
        const message = data.message || 'Could not add items to cart';
        this.#setLiveRegionText(message);
        deferredEventPromise.reject(new Error(message));
        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        return;
      }

      const cart = await this.#refreshCart();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'frequently-bought-together-component',
          sourceId: this.id?.toString(),
          itemCount: items.length,
          didError: false,
        },
      });

      this.#setLiveRegionText(Theme.translations?.added || 'Added to cart');
    } catch (error) {
      console.error('[frequently-bought-together]', error);
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error while adding to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      addButton.disabled = false;
      addButton.removeAttribute('data-loading');
    }

    if (liveRegion) {
      setTimeout(() => this.#setLiveRegionText(''), 5000);
    }
  }

  /** @returns {HTMLInputElement[]} */
  #getCheckedCheckboxes() {
    const { checkboxes } = this.refs;
    if (!checkboxes) return [];

    const list = Array.isArray(checkboxes) ? checkboxes : [checkboxes];
    return list.filter(
      /** @param {any} checkbox */ (checkbox) => checkbox instanceof HTMLInputElement && checkbox.checked && !checkbox.disabled
    );
  }

  #updateTotal() {
    const { totalDisplay } = this.refs;
    if (!totalDisplay) return;

    const totalCents = this.#getCheckedCheckboxes().reduce(
      (sum, checkbox) => sum + (parseInt(checkbox.dataset.price ?? '', 10) || 0),
      0
    );

    totalDisplay.textContent = this.#formatMoney(totalCents);
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const { moneyFormat } = this.refs;
    const format =
      moneyFormat instanceof HTMLTemplateElement ? moneyFormat.content.textContent || '${{amount}}' : '${{amount}}';
    const currency = this.dataset.currency || 'USD';

    return formatMoney(cents, format, currency);
  }

  /**
   * Refreshes the cart drawer contents if present, otherwise fetches the cart JSON directly.
   */
  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /** @param {string} text */
  #setLiveRegionText(text) {
    const { liveRegion } = this.refs;
    if (liveRegion) liveRegion.textContent = text;
  }
}

if (!customElements.get('frequently-bought-together-component')) {
  customElements.define('frequently-bought-together-component', FrequentlyBoughtTogetherComponent);
}
